import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { UserService } from '~services/user.service';
import { EmailSettingsV2Service } from '~services/email-settings-v2.service';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { LoaderIntegrationLoad, PurchaseOrderData } from '~models/emailSettings.model';

@Component({
  selector: 'sa-loads-for-purchase-order',
  templateUrl: './loads-for-purchase-order.component.html',
  styleUrls: ['./loads-for-purchase-order.component.scss'],
})
export class LoadsForPurchaseOrderComponent implements OnInit, OnDestroy {
  activeDataSource: MatTableDataSource<LoaderIntegrationLoad>;
  displayedColumns = [
    'uploadTime',
    'bolNumber',
    'loadWeight',
    'pickupTime',
    'truckNumber',
    'potentialMatchCount',
    'orderId',
    'matchReasons',
  ];
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  subscriptions: Subscription[] = [];

  public goodsReceiptEditingRow: LoaderIntegrationLoad | null = null;

  public goodsIssuedEditingRow: LoaderIntegrationLoad | null = null;

  public filter = 'all';

  private filter$$ = new BehaviorSubject<string>('all');

  public loaderTimezone: string;

  public purchaseOrderData$: Observable<PurchaseOrderData>;

  constructor(
    private route: ActivatedRoute,
    public emailSettingsService: EmailSettingsV2Service,
    public userService: UserService,
    public router: Router,
  ) {}

  ngOnInit() {
    this.activeDataSource = new MatTableDataSource([]);
    this.activeDataSource.sort = this.sort;

    this.purchaseOrderData$ = this.emailSettingsService.purchaseOrderData$;

    this.filter = this.route.snapshot.queryParams['filter'];
    if (this.filter !== 'matched' && this.filter !== 'unmatched' && this.filter !== 'all') {
      this.router.navigate([], { queryParams: { filter: 'all' } });
      this.filter = 'all';
    }

    this.filter$$.next(this.filter);

    this.subscriptions.push(
      combineLatest([this.emailSettingsService.loadsForPurchaseOrder$, this.filter$$]).subscribe(([loads, filter]) => {
        if (loads) {
          this.activeDataSource.data = this.filterLoads(loads, filter);
        } else {
          this.activeDataSource.data = [];
        }
      }),
    );

    this.subscriptions.push(
      this.emailSettingsService.purchaseOrderData$.subscribe((purchaseOrderData) => {
        if (purchaseOrderData) {
          this.loaderTimezone = purchaseOrderData.loaderTimezone;
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subs) => subs.unsubscribe());
  }

  public toMatchReasonText(matchReason: string, loadData: LoaderIntegrationLoad): string {
    const matchReasonMap = {
      site_visit: 'Mine Site Visit Time',
      bol_number: 'BOL Number',
      po_number: 'PO Number',
      truck_number: 'Truck Number',
      exact_load_weight: 'Exact Load Weight',
    };

    if (matchReason === 'partial_truck_number') {
      return `Partial Truck Number (${loadData.partialTruckNumberMatchLength} chars)`;
    }

    return matchReasonMap[matchReason];
  }

  public filterTypeChange() {
    this.router.navigate([], { queryParams: { filter: this.filter }, skipLocationChange: true });
    this.filter$$.next(this.filter);
  }

  private filterLoads(loads: LoaderIntegrationLoad[], filter: string): LoaderIntegrationLoad[] {
    if (
      !this.displayedColumns.includes('goodsReceipt') &&
      !this.displayedColumns.includes('goodsIssued') &&
      this.filter !== 'unmatched'
    ) {
      this.displayedColumns.push('goodsReceipt', 'goodsIssued');
    }

    if (filter === 'matched') {
      return loads.filter((load) => load.matched);
    }

    if (filter === 'unmatched') {
      const itemsToRemove: string[] = ['goodsReceipt', 'goodsIssued'];
      this.displayedColumns = this.displayedColumns.filter((item) => !itemsToRemove.includes(item));
      return loads.filter((load) => !load.matched);
    }

    return loads;
  }
}
